import { useEffect, useMemo, useState } from "react";
import { Modal, Form, Badge } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { InfoCircleFill, XCircle } from "react-bootstrap-icons";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { nanoid } from "nanoid";

export enum RuleType {
  Discount = 0,
  Fee = 1,
}

export type RuleUI = ContractRuleForm & {
  id: string;
};

export type ContractRuleForm = {
  min?: number;
  max?: number;
  noCost: boolean;
  type: RuleType;
  price?: number;

  _priceText?: string;
  _isPriceFocused?: boolean;
};

export type ContractRuleContext =
  | { mode: "plan" }
  | { mode: "group"; groupId: number | null };

/** Plan monthly unit price in cents — not CachedMrr / subscription.price. */
export function resolvePlanBasePriceCents(
  prices: { planPriceId: number; price: number }[] | null | undefined,
  planPriceId: number | null | undefined,
  fallbackCents = 0,
): number {
  const match = prices?.find((p) => p.planPriceId === planPriceId);
  return match != null ? match.price : fallbackCents;
}

export interface ContractRuleModalProps {
  show: boolean;
  onClose: () => void;
  context: ContractRuleContext;
  groups?: { id: number; name: string }[];
  basePriceCents: number;
  onSubmit: (data: {
    groupId: number;
    rules: ContractRuleForm[];
  }) => Promise<void>;
  isEditing: boolean;

  initialRules?: {
    min?: number;
    max?: number;
    price?: number;
    type?: RuleType;
  }[];

  onRemoveContract: (groupId: number) => Promise<void>;
}

export function ContractRuleModal({
  show,
  onClose,
  context,
  groups = [],
  basePriceCents,
  onSubmit,
  isEditing,
  initialRules,
  onRemoveContract,
}: ContractRuleModalProps) {
  const [groupId, setGroupId] = useState<number | null>(null);
  const [rules, setRules] = useState<ContractRuleForm[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [newlyAddedRuleIndex, setNewlyAddedRuleIndex] = useState<number | null>(
    null,
  );

  const isPlanMode = context.mode === "plan";
  const getRuleType = (finalPrice: number) =>
    finalPrice > basePriceCents ? RuleType.Fee : RuleType.Discount;

  const getAdjustmentAmount = (finalPrice: number) =>
    finalPrice === 0 ? 0 : Math.abs(finalPrice - basePriceCents);

  const getFinalPrice = (adjustment?: number, type?: RuleType) => {
    if (adjustment == null || adjustment === 0) return 0;

    const amount = Math.abs(adjustment);
    const ruleType =
      type ?? (adjustment > 0 ? RuleType.Fee : RuleType.Discount);

    return ruleType === RuleType.Fee
      ? basePriceCents + amount
      : Math.max(basePriceCents - amount, 0);
  };

  const selectedGroupName = useMemo(() => {
    if (context.mode === "group" && context.groupId) {
      return groups.find((g) => g.id === context.groupId)?.name ?? null;
    }
    return null;
  }, [context, groups]);

  useEffect(() => {
    if (context.mode === "group") {
      setGroupId(context.groupId);
    } else {
      setGroupId(null);
    }

    if (isEditing && initialRules?.length! > 0) {
      setRules(
        initialRules!.map((r) => ({
          min: r.min ?? undefined,
          max: r.max ?? undefined,
          noCost: r.price == null || r.price === 0,
          price: getFinalPrice(r.price, r.type),
          type: r.type ?? (r.price! > 0 ? RuleType.Fee : RuleType.Discount),
        })),
      );
    } else {
      setRules([]);
    }
    setError(null);
  }, [show, context, basePriceCents]);

  const rangesOverlap = (a: ContractRuleForm, b: ContractRuleForm) => {
    const minA = a.min ?? -Infinity;
    const maxA = a.max ?? Infinity;
    const minB = b.min ?? -Infinity;
    const maxB = b.max ?? Infinity;

    return minA <= maxB && minB <= maxA;
  };

  const validateRules = () => {
    if (rules.length === 0) {
      return "At least one rule must be added.";
    }
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];

      if (r.min == null) {
        return "Each rule must have a From value.";
      }

      if (r.min <= 0) {
        return "From must be greater than 0.";
      }

      if (r.min != null && r.max != null && r.max < r.min) {
        return "Maximum volume cannot be less than minimum volume.";
      }

      if (r.price == null || r.price < 0) {
        return "Final price is required.";
      }

      if (r.price === basePriceCents) {
        return "Final price cannot equal the base price. Leave that range uncovered to use the base rate.";
      }

      for (let j = i + 1; j < rules.length; j++) {
        if (rangesOverlap(r, rules[j])) {
          return "Rule volume ranges cannot overlap.";
        }
      }
    }

    return null;
  };

  function createEmptyRule() {
    return {
      min: undefined,
      max: undefined,
      noCost: false,
      type: RuleType.Discount,
      price: undefined,
      id: nanoid(),

      // UI-only fields
      _priceText: undefined,
      _isPriceFocused: false,
    };
  }

  function addRule() {
    const newRule = createEmptyRule();
    setRules((prev) => {
      const updated = [...prev, newRule].sort((a, b) => {
        const aMin = a.min ?? Number.MAX_SAFE_INTEGER;
        const bMin = b.min ?? Number.MAX_SAFE_INTEGER;
        return aMin - bMin;
      });
      // Find the index of the newly added rule after sorting
      const newIndex = updated.findIndex(
        (r) => (r as any).id === (newRule as any).id,
      );
      if (newIndex !== -1) {
        setNewlyAddedRuleIndex(newIndex);
      }
      return updated;
    });
  }

  const updateRule = (index: number, patch: Partial<ContractRuleForm>) => {
    setError(null);
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );
  };

  const removeRule = (index: number) =>
    setRules((r) => r.filter((_, i) => i !== index));

  const prepareRulesForSubmit = () =>
    rules.map((rule) => ({
      ...rule,
      noCost: rule.price === 0,
      type: getRuleType(rule.price ?? 0),
      price: getAdjustmentAmount(rule.price ?? 0),
    }));

  const submit = async () => {
    setError(null);

    const validationError = validateRules();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!groupId) {
      setError("Group is required.");
      return;
    }

    try {
      setSaving(true);
      await onSubmit({
        groupId,
        rules: prepareRulesForSubmit(),
      });
      reorderRules();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const sortedRules = useMemo(() => {
    return [...rules].sort((a, b) => {
      const aMin = a.min ?? Number.MAX_SAFE_INTEGER;
      const bMin = b.min ?? Number.MAX_SAFE_INTEGER;
      return aMin - bMin;
    });
  }, [rules]);

  const reorderRules = () => {
    setRules((r) => [...r].sort((a, b) => (a.min ?? 0) - (b.min ?? 0)));
  };

  const gaps = useMemo(() => {
    return sortedRules
      .map((r, i) => {
        if (i === 0) return null;

        const prevMax = sortedRules[i - 1].max ?? 0;
        const curMin = r.min ?? 0;

        if (curMin > prevMax + 1) {
          return {
            from: prevMax + 1,
            to: curMin - 1,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [sortedRules]);

  const formatCurrency = (cents?: number) =>
    cents != null
      ? (cents / 100).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })
      : "";

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div
            className="text-muted fw-semibold text-uppercase"
            style={{ fontSize: "0.65rem", letterSpacing: "0.08em" }}
          >
            Contract
          </div>
          <div>{selectedGroupName ?? (isEditing ? "Edit Contract" : "Add Contract")}</div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Base price info banner */}
        <div
          className="d-flex align-items-start gap-2 rounded px-3 py-2 mb-4 small"
          style={{ background: "rgba(214, 51, 132, 0.07)", color: "#a01060" }}
        >
          <InfoCircleFill style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            Parkers not matched to any tier are billed the{" "}
            <strong>base price of {formatCurrency(basePriceCents)} / month</strong>.
          </span>
        </div>

        {isPlanMode && (
          <Form.Group className="mb-3">
            <Form.Label>Group</Form.Label>
            <Form.Select
              value={groupId ?? ""}
              onChange={(e) => setGroupId(Number(e.target.value))}
            >
              <option value="" disabled>
                Select group
              </option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        <div className="mb-3">
          <h6 className="mb-1">Pricing tiers</h6>
          <div className="text-muted small">
            Set final monthly price based on parker count in the group
          </div>
        </div>

        {rules.length === 0 && (
          <div className="d-flex flex-column align-items-center justify-content-center text-center py-4 border rounded mb-3">
            <div
              className="rounded-circle mb-3"
              style={{
                width: 56,
                height: 56,
                backgroundColor: "rgba(214, 51, 132, 0.1)",
              }}
            />
            <p className="text-secondary mb-3 small">No tiers added yet</p>
            <Button size="sm" variant="outline-primary" onClick={addRule}>
              + Add Tier
            </Button>
          </div>
        )}

        {rules.map((rule, i) => (
          <div
            key={i}
            className="rounded p-3 mb-3"
            style={{ border: "1.5px solid rgba(214,51,132,0.3)", background: "rgba(214,51,132,0.02)" }}
          >
            {/* Tier header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Badge
                bg=""
                style={{
                  border: "1.5px solid #d63384",
                  color: "#d63384",
                  background: "transparent",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              >
                Tier {i + 1}
              </Badge>
              <button
                className="btn btn-sm btn-link text-danger text-decoration-none p-0 d-flex align-items-center gap-1"
                onClick={() => removeRule(i)}
              >
                <XCircle size={14} />
                Remove
              </button>
            </div>

            {/* PARKER COUNT RANGE label */}
            <div
              className="text-muted fw-semibold text-uppercase mb-2"
              style={{ fontSize: "0.65rem", letterSpacing: "0.07em" }}
            >
              Parker Count Range
            </div>

            {/* Fields row */}
            <div className="d-flex align-items-end gap-2 flex-wrap mb-3">
              <div style={{ width: 90 }}>
                <Form.Label className="small mb-1">From</Form.Label>
                <Form.Control
                  ref={(el) => {
                    if (i === newlyAddedRuleIndex && el) {
                      setTimeout(() => el.focus(), 0);
                      setNewlyAddedRuleIndex(null);
                    }
                  }}
                  type="number"
                  value={rule.min ?? ""}
                  onChange={(e) =>
                    updateRule(i, {
                      min: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                />
              </div>

              <span className="pb-2 text-muted">—</span>

              <div style={{ width: 140 }}>
                <Form.Label className="small mb-1">To</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="No limit (∞)"
                  value={rule.max ?? ""}
                  onChange={(e) =>
                    updateRule(i, {
                      max: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                />
              </div>

              <div className="ms-auto" style={{ minWidth: 140 }}>
                <Form.Label className="small mb-1">Monthly price</Form.Label>
                <Form.Control
                  type="text"
                  disabled={rule.noCost}
                  value={
                    rule._isPriceFocused
                      ? (rule._priceText ?? "")
                      : rule.price != null
                        ? (rule.price / 100).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })
                        : ""
                  }
                  onFocus={() =>
                    updateRule(i, {
                      _isPriceFocused: true,
                      _priceText:
                        rule.price != null ? (rule.price / 100).toString() : "",
                    })
                  }
                  onChange={(e) =>
                    updateRule(i, {
                      _priceText: e.target.value.replace(/[^0-9.]/g, ""),
                    })
                  }
                  onBlur={() => {
                    const value = rule._priceText
                      ? Math.round(parseFloat(rule._priceText) * 100)
                      : undefined;

                    updateRule(i, {
                      _isPriceFocused: false,
                      price: isNaN(value as number) ? undefined : value,
                      noCost: value === 0,
                      _priceText: undefined,
                    });
                  }}
                />
              </div>
            </div>

            {/* Toggle + summary */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <Form.Switch
                label="Mark as free"
                checked={rule.noCost || rule.price === 0}
                onChange={(e) =>
                  updateRule(i, {
                    noCost: e.target.checked,
                    price: e.target.checked ? 0 : undefined,
                    _priceText: undefined,
                    _isPriceFocused: false,
                  })
                }
              />

              {rule.min != null && rule.price != null && (
                <span
                  className="badge rounded-pill"
                  style={{ background: "#d4edda", color: "#155724", fontSize: "0.75rem", fontWeight: 500 }}
                >
                  👥{" "}
                  {rule.max != null
                    ? `${rule.min}–${rule.max}`
                    : `${rule.min}+`}{" "}
                  parkers →{" "}
                  {rule.price === 0 ? "Free" : `${formatCurrency(rule.price)} / mo`}
                </span>
              )}
            </div>
          </div>
        ))}

        {gaps.map((gap, i) => (
          <div key={i} className="alert alert-muted-gold small">
            Gap detected: {gap!.from}–{gap!.to} parkers are not covered
          </div>
        ))}

        {rules.length > 0 && (
          <button
            className="btn w-100 text-primary fw-semibold"
            style={{
              border: "2px dashed #d63384",
              borderRadius: 8,
              color: "#d63384",
              background: "transparent",
            }}
            onClick={addRule}
          >
            + Add another tier
          </button>
        )}

        {error && <div className="alert alert-danger mt-3 small">{error}</div>}
      </Modal.Body>

      <Modal.Footer className="d-flex align-items-center">
        <div className="me-auto">
          {isEditing && (
            <Button
              variant="outline-danger"
              size="sm"
              disabled={saving || !groupId}
              onClick={() => setShowRemoveConfirm(true)}
            >
            
              Remove contract
            </Button>
          )}
        </div>

        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button className="btn-main" onClick={submit} disabled={saving}>
       
            Save contract
          </Button>
        </div>
      </Modal.Footer>

      <ConfirmModal
        title="Remove Contract"
        message="Are you sure you want to remove this contract?"
        show={showRemoveConfirm}
        setShow={setShowRemoveConfirm}
        onConfirm={async () => {
          if (!groupId) return;
          try {
            setSaving(true);
            await onRemoveContract(groupId);
            setShowRemoveConfirm(false);
            onClose();
          } finally {
            setSaving(false);
          }
        }}
      />
    </Modal>
  );
}
